
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { GetStaticPropsContext } from 'next/types';
import { Makeswift, Page as MakeswiftPage, PageProps as MakeswiftPageProps } from '@makeswift/runtime/next';
import { SWRConfig } from 'swr';
import { CategoriesDocument, CategoryFragment, GlossaryDocument, GlossaryFeedDocument, GlossaryFragment } from '@/generated/contentful';
import { client as contentful } from '@/lib/contentful/client';
import { serializeCacheKey } from '@/lib/contentful/utils';
import { getPageSnapshot } from '@/lib/makeswift/client';
import '@/lib/makeswift/components';
import { CategoryMeta } from '@/components/Contentful/entries/Category/CategoryMeta';
import { DEFAULT_LIMIT } from '@/components/Contentful/entries/Glossary/GlossaryFeed';
import { GlossaryMeta } from '@/components/Contentful/entries/Glossary/GlossaryMeta/GlossaryMeta';
import { getGlossaryCategories } from '@/components/Contentful/entries/Glossary/helpers/getGlossaryCategories';
import { PreviewToast } from '@/components/PreviewToast';
type Props = {
    fallback: {
        [key: string]: unknown;
    };
    preview: boolean;
    isGlossaryCategory: boolean;
    entryItem: CategoryFragment | GlossaryFragment;
} & MakeswiftPageProps;
export default function Glossary({ fallback, preview, snapshot, isGlossaryCategory, entryItem }: Props) {
    return (<SWRConfig value={{ fallback }}>
			<PreviewToast preview={preview}/>

			{isGlossaryCategory ? (<CategoryMeta entryItem={entryItem as CategoryFragment}/>) : (<GlossaryMeta entryItem={entryItem as GlossaryFragment}/>)}

			<MakeswiftPage snapshot={snapshot}/>
		</SWRConfig>);
}
async function getStaticProps({ previewData, preview, params, locale }: GetStaticPropsContext<{
    slug: string[] | undefined;
}, {
    makeswift: boolean;
}>) {
    const getSlug = () => {
        const slug = params?.slug?.at?.(-1);
        if (slug && !slug.startsWith('?'))
            return slug;
        return 'glossary';
    };
    const slug = getSlug();
    const categories = await getGlossaryCategories(0, 100, locale);
    const category = categories?.find((item) => item?.slug === slug) ??
        categories?.find((item) => item?.slug_en_US === slug);
    // Is glossary category page
    if (category?.slug) {
        const snapshot = await getPageSnapshot({
            pathname: '/glossary/category/__template__',
            isMakeswiftPreview: previewData?.makeswift == true,
            previewData,
            locale,
            timeout: {
                ms: 10000,
                message: `Timeout request getPageSnapshot for "/glossary/category/__template__" – Locale: ${locale}`
            }
        });
        if (snapshot == null) {
            return { notFound: true, revalidate: 3600 };
        }
        const glossaryFeed = await contentful({ preview: Boolean(preview) }).request(GlossaryFeedDocument, {
            where: { category: { slug: category.slug }, enabled: true },
            limit: DEFAULT_LIMIT,
            locale,
            skip: 0,
            preview: Boolean(preview)
        });
        const categoriesEntries = await contentful({ preview: Boolean(preview) }).request(CategoriesDocument, {
            where: { slug },
            limit: 1,
            locale
        });
        return {
            props: {
                snapshot,
                fallback: {
                    [serializeCacheKey('glossaryCategories', { limit: 100, locale })]: categories,
                    [serializeCacheKey('glossaryFeed', { locale })]: glossaryFeed
                },
                previewMode: Makeswift.getPreviewMode(previewData),
                preview: Boolean(preview),
                isGlossaryCategory: true,
                entryItem: categoriesEntries.categoryCollection?.items[0]
            },
            revalidate: 3600
        };
    }
    const glossary = await contentful({ preview: Boolean(true) }).request(GlossaryDocument, {
        where: { slug, enabled: true },
        skip: 0,
        locale,
        preview: Boolean(preview)
    });
    // 404 as category or glossary not found
    if (!glossary?.glossaryCollection?.items?.length) {
        return {
            notFound: true,
            revalidate: 3600
        };
    }
    const snapshot = await getPageSnapshot({
        pathname: '/glossary/__template__',
        isMakeswiftPreview: previewData?.makeswift == true,
        previewData,
        locale,
        timeout: {
            ms: 10000,
            message: `Timeout request getPageSnapshot for "/glossary/__template__" – Locale: ${locale}`
        }
    });
    if (snapshot == null) {
        return { notFound: true, revalidate: 3600 };
    }
    return {
        props: {
            snapshot,
            fallback: {
                [serializeCacheKey('glossaryCategories', { limit: 100, locale })]: categories,
                [serializeCacheKey('glossary', { locale, category: slug })]: glossary
            },
            previewMode: Makeswift.getPreviewMode(previewData),
            preview: Boolean(preview),
            isGlossaryCategory: false,
            entryItem: glossary?.glossaryCollection?.items[0]
        },
        revalidate: 3600
    };
}
export async function getStaticPaths() {
    return {
        paths: [],
        fallback: 'blocking'
    };
}

    async function __Next_Translate__getStaticProps__1961751ef44__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/glossary/[[...slug]]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || (() => Promise.resolve({})),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1961751ef44__ as getStaticProps }
  